import { useState } from 'react';

export default function HeroSection() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyCondition: '',
    estimatedValue: '',
    propertyAddress: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formElement = e.target as HTMLFormElement;
      const submitAddr = formElement.action;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We\'ll contact you within 24 hours with a cash offer.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          propertyCondition: '',
          estimatedValue: '',
          propertyAddress: '',
        });
      } else {
        setSubmitMessage('Something went wrong. Please call us directly at (915) 555-1234.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please call us directly at (915) 555-1234.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="relative bg-gradient-to-br from-teal-50 to-white pt-32 pb-20 px-4">
      <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Need to Sell Your Distressed Property Fast?
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 font-semibold max-w-3xl mx-auto">
            Submit your property details below and receive a fair cash offer within 24 hours - no repairs, no fees, no hassle
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-full mb-4">
              <i className="ri-home-heart-line text-3xl text-white"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Get Your Cash Offer</h2>
            <p className="text-gray-600 mt-2">Fast, fair, and completely free</p>
          </div>

          <form
            className="space-y-5"
            data-readdy-form
            id="distressed-property-hero-form"
            onSubmit={handleSubmit}
            action="https://readdy.ai/api/form/d5lndqh44v703eh90t2g"
          >
            <div>
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                required
                value={formData.fullName}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <select
                name="propertyCondition"
                required
                value={formData.propertyCondition}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
              >
                <option value="">What's the property condition?</option>
                <option value="excellent">Excellent - Move-in Ready</option>
                <option value="good">Good - Minor Repairs Needed</option>
                <option value="fair">Fair - Moderate Repairs Needed</option>
                <option value="poor">Poor - Major Repairs Needed</option>
                <option value="uninhabitable">Uninhabitable - Extensive Work Required</option>
              </select>
            </div>
            <div>
              <input
                type="text"
                name="estimatedValue"
                placeholder="Estimated Property Value (Optional)"
                value={formData.estimatedValue}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <input
                type="text"
                name="propertyAddress"
                placeholder="Property Address"
                required
                value={formData.propertyAddress}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-900 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
            </button>
          </form>

          {submitMessage && (
            <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('Thank you') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {submitMessage}
            </div>
          )}

          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm">
              Prefer to talk? <a href="tel:9155551234" className="text-teal-600 font-semibold hover:text-teal-700 cursor-pointer">Call us now at (915) 555-1234</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
